/**
 * AdvicePanel.jsx — Live trading framework briefing card
 *
 * Regime → structure mapping (the correct framework):
 *   Trend day    → BUY a debit spread in the trend direction
 *   Range/Chop   → SELL a credit spread, collect premium
 *   Slow Grind   → SELL a directional credit spread WITH the drift
 *   Whipsaw      → Debit spread only (known max loss), tiny size — or sit out
 *
 * Time phases gate the instruction — you never enter before the opening drive confirms.
 */

import { classifySessionRegime } from '../../lib/sessionRegime';

// ── Mode definitions ──────────────────────────────────────────────────────────

const MODES = {
  DEBIT: {
    key: 'DEBIT',
    label: '📈 Buy a Debit Spread',
    short: 'BUY DEBIT SPREAD',
    color: '#22c55e',
    bg: '#22c55e10',
    summary: 'Trend day — buy a call or put spread in the direction of the move. You pay a fixed amount upfront and profit if the stock keeps moving your way.',
  },
  CREDIT: {
    key: 'CREDIT',
    label: '💰 Sell a Credit Spread',
    short: 'SELL CREDIT SPREAD',
    color: '#818cf8',
    bg: '#818cf810',
    summary: 'Range or chop day — sell options and collect money upfront. The market going sideways works for you. Time decay earns you money every hour.',
  },
  DIRSPREAD: {
    key: 'DIRSPREAD',
    label: '↗ Sell Directional Credit Spread',
    short: 'SELL WITH THE DRIFT',
    color: '#f59e0b',
    bg: '#f59e0b10',
    summary: 'Slow grind — sell a credit spread in the SAME direction as the drift. If it\'s drifting up, sell a put spread below. This lets the drift work for you. Selling against the grind (fading it) is the wrong side every time.',
  },
  DEBIT_SMALL: {
    key: 'DEBIT_SMALL',
    label: '⚠ Debit Spread Only — Tiny Size',
    short: 'DEBIT ONLY / TINY',
    color: '#f97316',
    bg: '#f97316 10',
    summary: 'Whipsaw — if you must trade, a debit spread is the only safe structure. You pay a fixed amount and that\'s your worst case. Never sell credit spreads on a whipsaw day — the move and reversal can hit both sides.',
  },
  SITOUT: {
    key: 'SITOUT',
    label: '🚫 Sit Out',
    short: 'SIT OUT TODAY',
    color: '#ef4444',
    bg: '#ef444410',
    summary: 'Whipsaw — both sides of the market are fighting. Best outcome is staying flat. A debit spread is acceptable if you absolutely must trade, but the preferred choice is no trade.',
  },
};

// ── Spread type recommendation ────────────────────────────────────────────────

function getSpreadType(regime, pm, mode) {
  const net    = regime?.metrics?.netMovePct ?? 0;
  const pmDir  = pm?.priorDir;
  const bullBias = net > 0.15 || (pmDir === 'up'   && net >= 0);
  const bearBias = net < -0.15 || (pmDir === 'down' && net <= 0);

  if (mode?.key === 'DEBIT' || mode?.key === 'DEBIT_SMALL') {
    // Trend/whipsaw → debit spread in trend direction
    if (bullBias) return { type:'call_debit', name:'Call Debit Spread', color:'#22c55e', icon:'↑',
      plain:'Buy a call spread — pay upfront, profit if price moves up. Max loss is exactly what you paid.' };
    if (bearBias) return { type:'put_debit', name:'Put Debit Spread', color:'#ef4444', icon:'↓',
      plain:'Buy a put spread — pay upfront, profit if price moves down. Max loss is exactly what you paid.' };
    return { type:'call_debit', name:'Call or Put Debit Spread', color:'#f59e0b', icon:'⟺',
      plain:'Direction unclear — wait for the opening drive to confirm before buying.' };
  }

  if (mode?.key === 'DIRSPREAD') {
    // Slow grind — credit spread in the SAME direction as drift
    if (bullBias) return { type:'bull_put', name:'Bull Put Spread (sell)', color:'#22c55e', icon:'↑',
      plain:'Grind is UP — sell a put spread below the market. Collect premium. The drift works for you. Do not sell a call spread against the grind.' };
    if (bearBias) return { type:'bear_call', name:'Bear Call Spread (sell)', color:'#ef4444', icon:'↓',
      plain:'Grind is DOWN — sell a call spread above the market. Collect premium. The drift works for you. Do not sell a put spread against the grind.' };
    return { type:'bull_put', name:'Directional Credit Spread', color:'#f59e0b', icon:'↗',
      plain:'Wait for grind direction to confirm before selecting spread side.' };
  }

  // Range / credit mode → iron condor or directional credit
  if (bullBias) return { type:'bull_put', name:'Bull Put Spread (sell)', color:'#22c55e', icon:'↑',
    plain:'Slight up bias — sell a put spread below the market. You keep the premium as long as price stays up.' };
  if (bearBias) return { type:'bear_call', name:'Bear Call Spread (sell)', color:'#ef4444', icon:'↓',
    plain:'Slight down bias — sell a call spread above the market. You keep the premium as long as price stays down.' };
  return { type:'iron_condor', name:'Iron Condor (sell both sides)', color:'#818cf8', icon:'⟺',
    plain:'Neutral range — sell a spread above AND below the market. Profit if nothing dramatic happens.' };
}

// ── Detectors ─────────────────────────────────────────────────────────────────

function detectWhipsaw(bars, minutesIn) {
  if (!bars || bars.length < 20 || minutesIn < 20) return false;
  const opening   = bars.slice(0, Math.min(15, bars.length));
  const openHigh  = Math.max(...opening.map(b => b.high));
  const openLow   = Math.min(...opening.map(b => b.low));
  const openRange = openHigh - openLow;
  if (openRange < 0.5) return false;
  const openPrice = opening[0].open ?? opening[0].close;
  const cur       = bars[bars.length - 1].close;
  return ((openHigh - openPrice) > openRange * 0.65 && cur < openPrice) ||
         ((openPrice - openLow)  > openRange * 0.65 && cur > openPrice);
}

function isSlowGrind(regime) {
  if (!regime || regime.regime !== 'mixed') return false;
  const { netMovePct, vwapRejections, efficiencyRatio } = regime.metrics ?? {};
  return netMovePct > 0.20 && vwapRejections <= 2 && efficiencyRatio >= 0.25;
}

function resolveMode(regime, bars, minutesIn) {
  if (!regime || regime.regime === 'forming') return null;
  if (detectWhipsaw(bars, minutesIn)) return MODES.DEBIT_SMALL;  // debit only, not full sit-out
  if (regime.regime === 'trend')   return MODES.DEBIT;
  if (regime.regime === 'range')   return MODES.CREDIT;
  if (isSlowGrind(regime))        return MODES.DIRSPREAD;
  return MODES.CREDIT; // mixed/unclear → cautious credit
}

function getPhase(marketOpen, minutesIn) {
  if (!marketOpen)     return 'premarket';
  if (minutesIn < 30)  return 'opening';
  if (minutesIn < 60)  return 'confirm';
  if (minutesIn < 90)  return 'active';
  return 'late';
}

// ── Exit rules (constant across all modes) ────────────────────────────────────
const EXIT_RULES = [
  'As soon as you enter, place a limit order to close at 50% profit. This often fills by noon on range days.',
  'If the 50% order hasn\'t filled by 3:00pm ET, close it at market. The remaining premium is not worth the end-of-day risk.',
];

// ── Advice content per phase × mode ───────────────────────────────────────────

function getAdvice(phase, mode, regime, pm, spreadType, vixPrice, price) {
  const priorH  = pm?.priorHigh;
  const priorL  = pm?.priorLow;
  const gap     = pm?.gapPct;
  const dailyADX = pm?.dailyADX;

  // PRE-MARKET ─────────────────────────────────────────────────────────────────
  if (phase === 'premarket') {
    if (pm?.coil?.coiling) return {
      modeLabel: 'Wait — No trade until the breakout', modeColor: '#ef4444',
      bullets: [
        `Price stuck in a $${pm.coil.bandLow}–$${pm.coil.bandHigh} range for ${pm.coil.daysInBand} days.`,
        `Wait for a clean break above $${pm.coil.bandHigh} or below $${pm.coil.bandLow}, then buy a debit spread in the breakout direction.`,
        'If no break by 10:00am — not worth trading today.',
      ],
    };
    if (gap != null && Math.abs(gap) >= 0.8) {
      const dir = gap > 0 ? 'UP' : 'DOWN';
      return {
        modeLabel: `📈 Likely BUY DEBIT SPREAD — Big gap ${dir}`, modeColor: '#22c55e',
        bullets: [
          `Gap ${dir === 'UP' ? 'up' : 'down'} ${Math.abs(gap).toFixed(2)}% overnight — signals a trend day. On trend days, buy a debit spread in the trend direction.`,
          `At the open: if price stays ${dir === 'UP' ? 'above' : 'below'} the opening level for the first 5–10 minutes, buy a call${dir==='UP'?'':' put'} spread. Short strike ~0.8–1% away from opening price.`,
          `Right after entry, place a limit order to close at 50% profit.`,
          `If gap reverses back through opening price: the trend read was wrong. Do not enter — consider a credit spread instead.`,
          vixPrice != null && vixPrice >= 18 ? `VIX ${vixPrice.toFixed(1)} — elevated premium means bigger expected moves. Good for debit spread buyers.` : '',
        ].filter(Boolean),
      };
    }
    if (vixPrice != null && vixPrice < 15 && (!dailyADX || dailyADX < 20)) return {
      modeLabel: '💰 Likely SELL CREDIT SPREAD — Calm, flat day expected', modeColor: '#818cf8',
      bullets: [
        `Small gap, VIX ${vixPrice?.toFixed(1)} low — market is likely to go sideways. Sell credit spreads.`,
        'Wait for the opening move to reverse back within 30 minutes — that confirms the range.',
        'Enter between 10:00–10:30am. Short strike at ~0.8–1% from today\'s price (roughly 30-delta in the morning).',
        'Right after entry, place a limit order to close at 50% profit. On range days this often fills by noon.',
        'If not filled by 3:00pm: close at market. End-of-day gamma risk is not worth holding.',
        spreadType ? `Recommended: ${spreadType.name}.` : '',
      ].filter(Boolean),
    };
    return {
      modeLabel: 'No clear signal — wait for the open', modeColor: '#f59e0b',
      bullets: [
        'No strong pre-market clues. Let the opening 30 minutes classify the day.',
        priorH && priorL ? `Watch yesterday\'s high ($${priorH}) and low ($${priorL}) — a break and hold of either confirms the direction.` : '',
        'If still unclear by 10:30am: skip today.',
      ].filter(Boolean),
    };
  }

  // OPENING DRIVE (9:30–10:00) ──────────────────────────────────────────────
  if (phase === 'opening') {
    const wait = `⏳ Watch only — do NOT enter yet. First 30 minutes is for reading the market, not trading.`;
    if (mode?.key === 'DEBIT_SMALL' || mode?.key === 'SITOUT') return {
      bullets: [
        '⚠ Market made a big move then reversed hard — this is a whipsaw day.',
        'If you cannot sit out: a debit spread is the only acceptable structure. You pay a fixed amount and that is your worst case. Never sell a credit spread on a whipsaw day — the reversal hits both sides.',
        'Keep size tiny (25% of normal or less). The regime has not resolved.',
      ],
    };
    if (mode?.key === 'DEBIT') return {
      bullets: [
        wait,
        `Trend forming. Look for: price staying on one side of VWAP for 15+ minutes AND each new candle making a new high (or low for downtrend).`,
        priorH ? `Key breakout level: yesterday\'s high $${priorH}. Two closes above it confirms the uptrend.` : '',
        'On confirmation, buy a debit spread. Short strike ~0.8–1% away from opening price.',
      ].filter(Boolean),
    };
    if (mode?.key === 'DIRSPREAD') return {
      bullets: [
        wait,
        `Slow drift forming. Do NOT fade it — that is the most expensive mistake on grind days.`,
        'Confirm the grind at 10:00am: if price has moved steadily in one direction with few reversals, sell a credit spread ON THE SAME SIDE as the drift.',
        'If drifting up: sell a put spread below. If drifting down: sell a call spread above.',
      ],
    };
    return {
      bullets: [
        wait,
        'Range forming — opening move is starting to reverse. Good conditions for selling credit.',
        'Confirm range at 10:00am (opening move reverses back within 30 minutes), then enter credit spread.',
        priorH && priorL ? `Prior day range $${priorL}–$${priorH} — expect price to stay in this zone.` : '',
      ].filter(Boolean),
    };
  }

  // CONFIRMATION (10:00–10:30) ───────────────────────────────────────────────
  if (phase === 'confirm') {
    if (mode?.key === 'DEBIT_SMALL' || mode?.key === 'SITOUT') return {
      bullets: [
        '⚠ Whipsaw confirmed. Best outcome is sitting out. If you absolutely must trade: debit spread only.',
        'Tiny size — 25% of normal max. Your max loss is the premium paid and nothing more.',
        'Never sell credit spreads on a whipsaw day. The initial move threatens one spread, then the reversal threatens the other.',
      ],
    };
    if (mode?.key === 'DEBIT') return {
      bullets: [
        '✅ Trend confirmed — enter now if you haven\'t.',
        spreadType ? `Buy a ${spreadType.type === 'call_debit' ? 'call' : 'put'} debit spread. ${spreadType.plain}` : 'Buy a debit spread in the trend direction.',
        'Short strike: ~0.8–1% away from today\'s current price. At 30-delta in the morning this is the right distance — the further from spot, the more room before gamma becomes dangerous.',
        'Immediately after entry: place a limit order to close at 50% profit.',
        'Stop rule: if price reverses back through VWAP within 60 minutes — exit the whole position. The trend read was wrong.',
      ],
    };
    if (mode?.key === 'DIRSPREAD') return {
      bullets: [
        '✅ Slow grind confirmed — sell a credit spread WITH the drift, not against it.',
        spreadType ? `${spreadType.icon} ${spreadType.plain}` : '',
        'Short strike ~0.8–1% from current price in the drift direction.',
        'Immediately after entry: place a limit order to close at 50% profit.',
        'If not filled by 3:00pm: close at market. End-of-day gamma is not worth it.',
      ].filter(Boolean),
    };
    return {
      bullets: [
        '✅ Range confirmed — enter your credit spread now (10:00–10:30am window).',
        spreadType ? `${spreadType.icon} ${spreadType.plain}` : '',
        'Short strike ~0.8–1% from current price. At 30-delta in the morning, this gives you room. The same strike in the afternoon would be worth almost nothing — morning entry is the correct timing.',
        'Immediately after entry: place a limit order to close at 50% profit. On range days this often fills by noon or early afternoon.',
        'If not filled by 3:00pm ET: close at market. The remaining premium is not worth the gamma risk at close.',
      ].filter(Boolean),
    };
  }

  // ACTIVE SESSION (10:30–11:00) ─────────────────────────────────────────────
  if (phase === 'active') {
    const exitReminder = 'Check your 50% profit limit order. If not filled by 3:00pm, close at market.';
    if (mode?.key === 'DEBIT_SMALL' || mode?.key === 'SITOUT') return {
      bullets: ['🚫 Whipsaw — no new trades. If holding a losing position: close it.',  exitReminder],
    };
    if (mode?.key === 'DEBIT') return {
      bullets: [
        'Trend day — hold your debit spread.',
        'Your 50% profit limit order should already be in. Let it work.',
        'If price reverses back through VWAP and holds below/above for two candles: the trend is done. Exit.',
        exitReminder,
      ],
    };
    return {
      bullets: [
        mode?.key === 'DIRSPREAD' ? 'Grind day — credit spread is working. Let time decay earn.' : 'Range day — credit spread is working. Time decay earns you money every hour.',
        'Your 50% profit limit order should already be in. Walk away.',
        'If short strike is within $1 of current price: watch closely and consider early exit.',
        exitReminder,
      ],
    };
  }

  // LATE (90+ min) ────────────────────────────────────────────────────────────
  if (mode?.key === 'DEBIT' || mode?.key === 'DEBIT_SMALL') return {
    bullets: [
      'If not in yet: late debit entries are poor risk/reward. Skip today.',
      'If in: hold to your stop or 50% target. Don\'t adjust mid-position.',
      '⏰ Close everything before 3:45pm ET. End-of-day gamma moves can be sharp and unpredictable.',
    ],
  };
  return {
    bullets: [
      'Late session — your 50% limit should be working.',
      '⏰ If NOT filled by 3:00pm: close at market NOW. Remaining premium is not worth the close-of-day risk.',
      'If filled already: you\'re done for the day. Good trade.',
    ],
  };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdvicePanel({ liveState, vixQuote, marketOpen }) {
  const bars      = liveState?.bars ?? [];
  const minutesIn = liveState?.minutesIntoSession ?? 0;
  const price     = liveState?.price;
  const vwap      = liveState?.vwap;
  const adx       = liveState?.adx;
  const pm        = liveState?.preMarket;
  const vixPrice  = vixQuote?.regularMarketPrice ?? vixQuote?.preMarketPrice ?? null;

  const phase      = getPhase(marketOpen, minutesIn);
  const regime     = (bars.length >= 10 && marketOpen) ? classifySessionRegime(bars, adx, vwap) : null;
  const mode       = resolveMode(regime, bars, minutesIn);
  const spreadType = getSpreadType(regime, pm, mode);
  const advice     = getAdvice(phase, mode, regime, pm, spreadType, vixPrice, price);

  const PHASE_META = {
    premarket: { label:'PRE-MARKET',     sub:'Before 9:30am ET — scan, don\'t trade',          color:'var(--faint)' },
    opening:   { label:'JUST OPENED',    sub:'9:30–10:00am ET — watch only, no trades yet',     color:'#f59e0b' },
    confirm:   { label:'ENTRY WINDOW',   sub:'10:00–10:30am ET — enter your trade or skip',     color:'#22c55e' },
    active:    { label:'ACTIVE SESSION', sub:'10:30–11:00am ET — manage what you have',         color:'#818cf8' },
    late:      { label:'LATE SESSION',   sub:'11:00am+ ET — close by 3pm, no new entries',     color:'var(--muted)' },
  };
  const pm2 = PHASE_META[phase];
  const borderColor = mode?.color ?? pm2.color ?? 'var(--border)';

  return (
    <div style={{ border:`1px solid ${borderColor}50`, borderRadius:8, marginBottom:14,
      background:mode?.bg ?? 'var(--bg)', overflow:'hidden' }}>

      {/* Top strip */}
      <div style={{ padding:'8px 14px', borderBottom:'1px solid var(--border)',
        background:'var(--bg-elev,var(--bg))', display:'flex',
        justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:9, fontWeight:800, letterSpacing:'.1em', padding:'2px 8px',
            borderRadius:3, background:pm2.color+'25', color:pm2.color, border:`1px solid ${pm2.color}40` }}>
            {pm2.label}
          </span>
          <span style={{ fontSize:9, color:'var(--faint)' }}>{pm2.sub}</span>
        </div>
        {mode
          ? <span style={{ fontSize:13, fontWeight:900, color:mode.color, letterSpacing:'.02em' }}>{mode.short}</span>
          : <span style={{ fontSize:11, color:'var(--faint)', fontStyle:'italic' }}>Reading market type…</span>
        }
      </div>

      <div style={{ padding:'12px 14px' }}>
        {/* Pre-market custom label */}
        {phase === 'premarket' && advice.modeLabel && (
          <div style={{ marginBottom:10 }}>
            <span style={{ fontSize:11, fontWeight:800, color:advice.modeColor }}>{advice.modeLabel}</span>
          </div>
        )}

        {/* Live regime strip */}
        {phase !== 'premarket' && regime && regime.regime !== 'forming' && (
          <div style={{ display:'flex', gap:'4px 16px', flexWrap:'wrap', marginBottom:8,
            fontSize:10, fontFamily:'monospace' }}>
            <span style={{ color:regime.color, fontWeight:700 }}>{regime.label}</span>
            <span style={{ color:'var(--faint)' }}>confidence {regime.confidence}%</span>
            {regime.metrics?.vwapRejections != null && (
              <span style={{ color:'var(--faint)' }}>{regime.metrics.vwapRejections} avg-price crosses</span>
            )}
          </div>
        )}
        {phase !== 'premarket' && regime?.regime === 'forming' && (
          <div style={{ fontSize:10, color:'var(--faint)', marginBottom:8 }}>
            Reading market — {bars.length} price bars collected. Need at least 10.
          </div>
        )}

        {/* Mode summary */}
        {mode && (
          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10, lineHeight:1.55,
            padding:'6px 10px', borderRadius:5, background:mode.color+'10',
            borderLeft:`3px solid ${mode.color}` }}>
            {mode.summary}
          </div>
        )}

        {/* Spread recommendation */}
        {spreadType && phase !== 'opening' && (
          <div style={{ marginBottom:10, padding:'8px 12px', borderRadius:6,
            border:`1px solid ${spreadType.color}50`, background:spreadType.color+'0c',
            display:'flex', alignItems:'flex-start', gap:10 }}>
            <span style={{ fontSize:18, lineHeight:1, flexShrink:0, color:spreadType.color }}>{spreadType.icon}</span>
            <div>
              <div style={{ fontSize:11, fontWeight:800, color:spreadType.color, marginBottom:2 }}>{spreadType.name}</div>
              <div style={{ fontSize:10, color:'var(--muted)', lineHeight:1.5 }}>{spreadType.plain}</div>
            </div>
          </div>
        )}
        {/* Show spread card in opening phase for early decision */}
        {spreadType && phase === 'opening' && (mode?.key === 'DEBIT' || mode?.key === 'DEBIT_SMALL') && (
          <div style={{ marginBottom:10, padding:'8px 12px', borderRadius:6,
            border:`1px solid ${spreadType.color}50`, background:spreadType.color+'0c',
            display:'flex', alignItems:'flex-start', gap:10 }}>
            <span style={{ fontSize:18, lineHeight:1, flexShrink:0, color:spreadType.color }}>{spreadType.icon}</span>
            <div>
              <div style={{ fontSize:11, fontWeight:800, color:spreadType.color, marginBottom:2 }}>Preparing to enter: {spreadType.name}</div>
              <div style={{ fontSize:10, color:'var(--muted)', lineHeight:1.5 }}>{spreadType.plain} — confirm direction before buying.</div>
            </div>
          </div>
        )}

        {/* Bullets */}
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {(advice.bullets ?? []).filter(Boolean).map((b, i) => (
            <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:11, lineHeight:1.55 }}>
              <span style={{ color:mode?.color ?? 'var(--faint)', flexShrink:0, fontWeight:800,
                fontSize:i===0?13:11, lineHeight:1.3, marginTop:1 }}>{i===0?'→':'·'}</span>
              <span style={{ color:b.startsWith('⚠')||b.startsWith('🚫')||b.startsWith('⏰') ? '#ef4444' : 'var(--text)' }}>
                {b}
              </span>
            </div>
          ))}
        </div>

        {/* Pre-market metrics footer */}
        {phase === 'premarket' && pm?.ready && (
          <div style={{ marginTop:10, display:'flex', gap:'3px 14px', flexWrap:'wrap',
            fontSize:9, fontFamily:'monospace', color:'var(--faint)',
            borderTop:'1px solid var(--border)', paddingTop:8 }}>
            {pm.gapPct!=null&&<span style={{color:Math.abs(pm.gapPct)>=0.8?'#22c55e':'var(--faint)'}}>Gap {pm.gapPct>0?'+':''}{pm.gapPct}%</span>}
            {pm.dailyADX!=null&&<span>Daily ADX {pm.dailyADX} {pm.dailyADX>=25?'(trending)':pm.dailyADX<18?'(ranging)':'(mixed)'}</span>}
            {vixPrice!=null&&<span>VIX {vixPrice.toFixed(1)} {vixPrice>=25?'⚠ high':vixPrice<15?'(calm)':''}</span>}
            {pm.priorHigh!=null&&<span>Prior high ${pm.priorHigh}</span>}
            {pm.priorLow!=null&&<span>Prior low ${pm.priorLow}</span>}
            {pm.coil?.coiling&&<span style={{color:'#ef4444',fontWeight:700}}>⊟ Coil active</span>}
          </div>
        )}
      </div>
    </div>
  );
}
